// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { BellPlus, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useSetUserAlertSubscriptionMutation } from "@/api/hooks/use-users";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

// Mirrors the server-side pattern: the sender fans a source out into its dotted prefixes, so a
// name outside this shape (uppercase, spaces, a stray dot) is a typo that would never fire.
const ALERT_NAME = /^[a-z0-9_]+(\.[a-z0-9_]+)*$/;

function subscribeAlertSchema(t: (key: string) => string) {
  return z.object({
    alert: z
      .string()
      .min(1, t("users.alerts.form.validation.required"))
      .max(64, t("users.alerts.form.validation.maxLength"))
      .regex(ALERT_NAME, t("users.alerts.form.validation.format")),
  });
}

type SubscribeAlertValues = z.infer<ReturnType<typeof subscribeAlertSchema>>;

interface SubscribeUserAlertDialogProps {
  publicId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Subscribes the user to an alert by name — the way onto an alert that has no row in
 * `messaging_alert_receivers` yet, and so does not appear in the table to be actioned.
 */
export function SubscribeUserAlertDialog({ publicId, open, onOpenChange }: SubscribeUserAlertDialogProps) {
  const { t } = useTranslation("back-office");
  const setSubscription = useSetUserAlertSubscriptionMutation(publicId);

  const schema = useMemo(() => subscribeAlertSchema(t), [t]);
  const form = useForm<SubscribeAlertValues>({
    resolver: zodResolver(schema),
    defaultValues: { alert: "" },
  });

  const prevOpen = useRef(false);
  useEffect(
    function resetFormOnDialogOpen() {
      if (open && !prevOpen.current) form.reset();
      prevOpen.current = open;
    },
    [open, form],
  );

  async function onSubmit(values: SubscribeAlertValues) {
    try {
      await setSubscription.mutateAsync({ alert: values.alert, isActive: true });
      toast.success(t("users.alerts.notifications.subscribed", { alert: values.alert }));
      onOpenChange(false);
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-subscribe-user-alert"
        className="sm:max-w-[34rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={BellPlus} />
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                <DialogHeader>
                  <DialogTitle>{t("users.alerts.dialog.subscribeTitle")}</DialogTitle>
                </DialogHeader>

                <p className="-mt-2 text-sm text-muted-foreground">{t("users.alerts.dialog.subscribeHint")}</p>

                <FormField
                  control={form.control}
                  name="alert"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          aria-label={t("users.alerts.form.alert")}
                          autoComplete="off"
                          data-1p-ignore
                          data-lpignore="true"
                          maxLength={64}
                          className="font-mono text-xs"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("buttons.cancel")}
              </Button>
              <Button type="submit" disabled={setSubscription.isPending}>
                {setSubscription.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <BellPlus className="h-4 w-4" />
                )}
                {t("users.alerts.dialog.subscribeAction")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
